/**
 * WeBelieveLogger - Universal Logging Class for the WeBelieve application
 * 
 * Provides structured logging with automatic batching, context enrichment,
 * and offline fallback support. All logs are sent to the backend for
 * storage in the webelieve_logs PostgreSQL table.
 * 
 * Features:
 * - Singleton pattern (one instance per application)
 * - Automatic batching (50 logs OR 5 seconds)
 * - Context enrichment (session, user, document, browser info)
 * - Offline fallback to IndexedDB
 * - Zero performance impact via async operations
 * 
 * @example
 * // Basic usage
 * WeBelieveLogger.info('cache', 'Cache initialized', { size: 1000 });
 * WeBelieveLogger.error('network', 'API request failed', { endpoint: '/api/docs' });
 * WeBelieveLogger.performance('wasm', 'paginate_from_cache', 45, { pages: 10 });
 * 
 * // Set user context after authentication
 * WeBelieveLogger.setContext({ user_id: 'uuid', realm_id: 'tenant-1' });
 * 
 * @see docs/10_Database/README.md
 */

import type {
  LogLevel,
  LogEvent,
  LogBatch,
  LogIngestResponse,
  WeBelieveLoggerConfig,
  LoggerContext,
  BrowserInfo,
  LoggerStats,
  OfflineLogEntry,
  SourceLocation,
  StackFrame,
} from './WeBelieveLogger.types';
import { decodeSourceLocation, preloadCurrentSourceMap } from './SourceMapDecoder';
import { LoggingConfigService } from '../services/LoggingConfigService';

// ============================================================================
// STACK TRACE CONFIGURATION
// ============================================================================

/** Maximum stack frames to capture (prevents huge payloads) */
const MAX_STACK_FRAMES = 30;

/** Patterns for internal frames to skip entirely */
const INTERNAL_SKIP_PATTERNS = [
  /captureCallerSource/,
  /captureFullStack/,
  /logWithSource/,
  /performanceWithSource/,
  /^WeBelieveLoggerClass\./,
];

/** Patterns for logger wrapper files - these should NOT be marked as culprit */
const LOGGER_WRAPPER_PATTERNS = [
  /WeBelieveLogger\.ts/,
  /WeBelieveLogger\.js/,
  /loggers\/(Wasm|Cache|Canvas|Input|Document|Api|Orchestrator)Logger\.(ts|js)/,
  /utils\/loggers\//,  // Any file in the loggers directory
];

/** Patterns for third-party/framework code */
const THIRD_PARTY_PATTERNS = [
  /node_modules/,
  /react-dom/,
  /react\.production/,
  /react\.development/,
  /scheduler/,
  /webpack/,
  /vite/,
  /@babel/,
  /regenerator-runtime/,
  // Production bundle patterns
  /chunk-/i,
  /vendor/i,
];

/** Patterns that indicate YOUR source code (dev mode) */
const USER_CODE_PATTERNS = [
  /src\/features\//,
  /src\/components\//,
  /src\/hooks\//,
  /src\/services\//,
  /src\/stores\//,
  /src\/pages\//,
  /src\/app\//,
];

/** Patterns that indicate YOUR source code in production bundles */
const USER_CODE_PRODUCTION_PATTERNS = [
  /index-[a-f0-9]+\.js/i,     // Main app bundle (Vite)
  /assets\/index/i,           // Vite asset bundles
  /main-[a-f0-9]+\.js/i,      // Alternative main bundle naming
  /app-[a-f0-9]+\.js/i,       // App bundle
  /\.tsx?:/,                  // TypeScript source (when source maps work)
];

/** Debug flag - set to true to debug stack trace capture */
const DEBUG_STACK_CAPTURE = false;

/**
 * Parse a single stack frame line into a StackFrame object
 */
function parseStackFrame(line: string): StackFrame | null {
  // Chrome format: "    at functionName (file:line:col)" or "    at file:line:col"
  let match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/);

  // Firefox format: "functionName@file:line:col"
  if (!match) {
    match = line.match(/(.+?)@(.+?):(\d+):(\d+)/);
  }

  if (!match) return null;

  const [, rawFunctionName, filePath, lineNum, colNum] = match;
  const functionName = rawFunctionName?.trim();

  // Parse class.method format
  let className: string | undefined;
  let methodName = functionName;
  if (functionName?.includes('.')) {
    const parts = functionName.split('.');
    if (parts.length === 2) {
      className = parts[0];
      methodName = parts[1];
    }
  }

  // Detect async frames
  const isAsync = line.includes('async ') || functionName?.startsWith('async ');

  // Store original file path for source map decoding later
  const originalFile = filePath || 'unknown';

  // Clean up file path for display
  let file = originalFile;
  if (file.includes('/src/')) {
    file = 'src/' + file.split('/src/').pop();
  } else if (file.includes('/node_modules/')) {
    file = 'node_modules/' + file.split('/node_modules/').pop();
  } else if (file.includes('/assets/')) {
    const assetMatch = file.match(/\/assets\/([^?]+)/);
    if (assetMatch) {
      file = 'assets/' + assetMatch[1];
    }
  } else if (file.includes('https://')) {
    // For production URLs, extract the bundle filename
    const urlMatch = file.match(/\/assets\/([^?]+)/);
    if (urlMatch) {
      file = 'assets/' + urlMatch[1];
    } else {
      const lastSegment = file.match(/\/([^\/]+)$/);
      if (lastSegment) {
        file = lastSegment[1];
      }
    }
  }

  // Determine if this is user code
  // Check both raw path and cleaned path, both dev and production patterns
  const isThirdParty = THIRD_PARTY_PATTERNS.some(p => p.test(filePath || '') || p.test(file));
  const isLoggerWrapper = LOGGER_WRAPPER_PATTERNS.some(p => p.test(filePath || '') || p.test(file));
  const isDevUserCode = USER_CODE_PATTERNS.some(p => p.test(filePath || '') || p.test(file));
  const isProdUserCode = USER_CODE_PRODUCTION_PATTERNS.some(p => p.test(filePath || '') || p.test(file));
  // User code = (dev patterns OR prod patterns) AND NOT third-party AND NOT logger wrapper
  const isUserCode = (isDevUserCode || isProdUserCode) && !isThirdParty && !isLoggerWrapper;

  return {
    file,
    line: parseInt(lineNum, 10) || 0,
    column: parseInt(colNum, 10) || 0,
    function: methodName || undefined,
    className,
    isUserCode,
    isAsync,
    raw: line.trim(),
  };
}

/**
 * Capture the full stack trace with rich metadata.
 * This is the premium version used for full stack trace capture.
 *
 * Features:
 * - Captures all frames (up to MAX_STACK_FRAMES)
 * - Identifies user code vs third-party code
 * - Finds the "culprit" frame (first user code after skipping internals)
 * - Preserves async boundaries
 * - Cleans up file paths for readability
 *
 * @param skipFrames - Additional frames to skip (default 1)
 * @returns SourceLocation with full stack array
 */
export function captureFullStack(skipFrames: number = 1): SourceLocation {
  try {
    const stack = new Error().stack;
    if (!stack) return { file: 'no-stack' };

    const lines = stack.split('\n');
    const frames: StackFrame[] = [];
    let culpritIndex = -1;
    let frameCount = 0;

    // Skip: Error line + captureFullStack = 2, plus any additional requested skips
    // We keep minimum skipping to capture the full call chain including React event handlers
    const framesToSkip = 2 + skipFrames;

    for (const line of lines) {
      // Skip the "Error" line
      if (line.trim().startsWith('Error')) continue;

      frameCount++;

      // Skip initial internal frames
      if (frameCount <= framesToSkip) continue;

      // Check if this frame should be completely skipped (internal logger functions)
      const shouldSkipEntirely = INTERNAL_SKIP_PATTERNS.some(
        p => p.test(line)
      );
      if (shouldSkipEntirely) continue;

      const frame = parseStackFrame(line);
      if (!frame) continue;

      // parseStackFrame already handles isUserCode detection including logger wrapper exclusion
      frames.push(frame);

      // Track the first user code frame as the culprit
      if (culpritIndex === -1 && frame.isUserCode) {
        culpritIndex = frames.length - 1;
      }

      // Limit total frames
      if (frames.length >= MAX_STACK_FRAMES) break;
    }

    // If no culprit found, try different strategies
    if (culpritIndex === -1) {
      // Strategy 1: Find first frame that's not clearly third-party
      culpritIndex = frames.findIndex(f => {
        const isThirdParty = THIRD_PARTY_PATTERNS.some(p => p.test(f.file) || p.test(f.raw || ''));
        return !isThirdParty;
      });
    }

    // Strategy 2: If still no culprit, just use the first frame
    if (culpritIndex === -1 && frames.length > 0) {
      culpritIndex = 0;
    }

    // Debug logging
    if (DEBUG_STACK_CAPTURE) {
      console.log('[captureFullStack] Frames captured:', frames.length);
      console.log('[captureFullStack] Culprit index:', culpritIndex);
      frames.forEach((f, i) => {
        console.log(`  [${i}] ${f.isUserCode ? '✓' : '✗'} ${f.file}:${f.line} (${f.function || 'anonymous'})`);
      });
    }

    // Get the primary location from the culprit frame
    const primaryFrame = frames[Math.max(0, culpritIndex)] || frames[0];

    return {
      file: primaryFrame?.file,
      line: primaryFrame?.line,
      column: primaryFrame?.column,
      function: primaryFrame?.function,
      stack: frames,
      stackDepth: frameCount - framesToSkip,
      stackTruncated: frameCount - framesToSkip > MAX_STACK_FRAMES,
      culpritIndex: culpritIndex >= 0 ? culpritIndex : 0,
    };
  } catch (e) {
    return { file: `error: ${e}` };
  }
}

/** Options for capturing caller source */
export interface CaptureSourceOptions {
  /** Additional frames to skip (default 1) */
  skipFrames?: number;
  /** Capture full stack trace (default false for performance) */
  includeFullStack?: boolean;
}

/**
 * Capture the caller's source location for passing to logger methods.
 * Call this at the top of wrapper functions to capture the actual caller.
 *
 * This function is smart about finding the actual caller:
 * 1. In development: Uses frame skipping to find the caller
 * 2. In production: Finds the first frame in YOUR source code (src/features, src/utils, etc.)
 *    by skipping internal logger frames and node_modules
 *
 * @param skipFramesOrOptions - Number of frames to skip OR options object
 * @returns SourceLocation object with file, line, column, function (and optionally full stack)
 *
 * @example
 * // Basic usage (no full stack):
 * const source = captureCallerSource();
 *
 * // With full stack trace:
 * const source = captureCallerSource({ includeFullStack: true });
 *
 * // With custom skip frames:
 * const source = captureCallerSource(2);
 * const source = captureCallerSource({ skipFrames: 2, includeFullStack: true });
 */
export function captureCallerSource(skipFramesOrOptions?: number | CaptureSourceOptions): SourceLocation {
  // Parse options
  const options: CaptureSourceOptions = typeof skipFramesOrOptions === 'number'
    ? { skipFrames: skipFramesOrOptions }
    : skipFramesOrOptions || {};

  const skipFrames = options.skipFrames ?? 1;
  // Always capture full stack by default (premium stack trace feature)
  // Set includeFullStack: false explicitly if you want lightweight capture
  const includeFullStack = options.includeFullStack ?? true;

  // If full stack requested (default), delegate to captureFullStack
  if (includeFullStack) {
    return captureFullStack(skipFrames);
  }

  // Otherwise, use the lightweight single-frame capture (opt-in only)
  try {
    const stack = new Error().stack;
    if (!stack) return { file: 'no-stack' };

    const lines = stack.split('\n');

    // Combine all skip patterns
    const skipPatterns = [
      ...INTERNAL_SKIP_PATTERNS,
      ...LOGGER_WRAPPER_PATTERNS,
      ...THIRD_PARTY_PATTERNS,
    ];

    let frameCount = 0;
    const baseSkip = 2 + skipFrames; // Minimum frames to skip
    let fallbackLocation: SourceLocation | null = null;

    for (const line of lines) {
      if (line.trim().startsWith('Error')) {
        continue;
      }

      frameCount++;

      const frame = parseStackFrame(line);
      if (!frame) continue;

      // Check if this frame should be skipped
      const shouldSkip = skipPatterns.some(pattern =>
        pattern.test(frame.file) || pattern.test(frame.function || '')
      );

      // Check if this is user code (use the already-computed isUserCode from parseStackFrame)
      // which handles both dev and production patterns
      const isUserCode = frame.isUserCode;

      const location: SourceLocation = {
        file: frame.file,
        line: frame.line,
        column: frame.column,
        function: frame.function,
      };

      // If this is YOUR source code and we've skipped enough frames, return it
      if (isUserCode && !shouldSkip && frameCount >= baseSkip) {
        return location;
      }

      // Store the first non-skipped frame as fallback
      if (!shouldSkip && frameCount >= baseSkip && !fallbackLocation) {
        fallbackLocation = location;
      }
    }

    return fallbackLocation || { file: 'no-source-match' };
  } catch (e) {
    return { file: `error: ${e}` };
  }
}

const DEFAULT_CONFIG: WeBelieveLoggerConfig = {
  batchSize: 50,
  flushIntervalMs: 5000,
  apiEndpoint: '/api/logs/ingest',
  enableDebug: import.meta.env.VITE_HEIGHT_INDEX_DEBUG === 'true',
  enableOfflineStorage: true,
  // Console output disabled - all logs are sent to backend logger
  enableConsoleOutput: false,
  appVersion: undefined,
  wasmVersion: undefined,
};

const INDEXEDDB_STORE_NAME = 'webelieve_offline_logs';
const INDEXEDDB_DB_NAME = 'WeBelieveLoggerDB';
const INDEXEDDB_VERSION = 1;

class WeBelieveLoggerClass {
  private static instance: WeBelieveLoggerClass | null = null;
  
  private config: WeBelieveLoggerConfig;
  private sessionId: string;
  private context: LoggerContext = {};
  private logBuffer: LogEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isFlushing = false;
  private db: IDBDatabase | null = null;
  
  private stats: LoggerStats = {
    totalLogsCreated: 0,
    totalLogsSent: 0,
    totalLogsDropped: 0,
    totalBatchesSent: 0,
    totalBatchesFailed: 0,
    offlineQueueSize: 0,
  };

  private constructor(config: Partial<WeBelieveLoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();
    this.initializeOfflineStorage();
    this.startFlushTimer();
    
    // Preload source map for current bundle
    preloadCurrentSourceMap();
    
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
    
    this.logInternal('info', 'logger', 'WeBelieveLogger initialized', {
      sessionId: this.sessionId,
      config: {
        batchSize: this.config.batchSize,
        flushIntervalMs: this.config.flushIntervalMs,
        enableDebug: this.config.enableDebug,
      },
    });
  }

  /**
   * Get the singleton instance of WeBelieveLogger
   */
  public static getInstance(config?: Partial<WeBelieveLoggerConfig>): WeBelieveLoggerClass {
    if (!WeBelieveLoggerClass.instance) {
      WeBelieveLoggerClass.instance = new WeBelieveLoggerClass(config);
    }
    return WeBelieveLoggerClass.instance;
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  public static resetInstance(): void {
    if (WeBelieveLoggerClass.instance) {
      WeBelieveLoggerClass.instance.shutdown();
      WeBelieveLoggerClass.instance = null;
    }
  }

  // ============================================================
  // PUBLIC API - Static methods for convenience
  // ============================================================

  /**
   * Log a debug message (disabled in production by default)
   */
  public static debug(component: string, message: string, data?: Record<string, unknown>): void {
    WeBelieveLoggerClass.getInstance().log('debug', component, message, data);
  }

  /**
   * Log an informational message
   */
  public static info(component: string, message: string, data?: Record<string, unknown>): void {
    WeBelieveLoggerClass.getInstance().log('info', component, message, data);
  }

  /**
   * Log a warning message
   */
  public static warn(component: string, message: string, data?: Record<string, unknown>): void {
    WeBelieveLoggerClass.getInstance().log('warn', component, message, data);
  }

  /**
   * Log an error message
   */
  public static error(component: string, message: string, data?: Record<string, unknown>): void {
    WeBelieveLoggerClass.getInstance().log('error', component, message, data);
  }

  /**
   * Log a performance metric
   */
  public static performance(
    component: string,
    operation: string,
    durationMs: number,
    data?: Record<string, unknown>
  ): void {
    WeBelieveLoggerClass.getInstance().logPerformance(component, operation, durationMs, data);
  }

  /**
   * Log a performance metric with a custom source (for forwarded logs like WASM)
   * Use this when the log originates from a different context (e.g., Web Worker)
   */
  public static performanceWithSource(
    component: string,
    operation: string,
    durationMs: number,
    source: SourceLocation,
    data?: Record<string, unknown>
  ): void {
    WeBelieveLoggerClass.getInstance().logPerformanceWithSource(component, operation, durationMs, source, data);
  }

  /**
   * Log a message with a custom source (for forwarded logs like WASM)
   * Use this when the log originates from a different context (e.g., Web Worker)
   */
  public static logWithSource(
    level: LogLevel,
    component: string,
    message: string,
    source: SourceLocation,
    data?: Record<string, unknown>
  ): void {
    WeBelieveLoggerClass.getInstance().logWithSource(level, component, message, source, data);
  }

  /**
   * Set global context (user_id, document_id, realm_id)
   */
  public static setContext(context: LoggerContext): void {
    WeBelieveLoggerClass.getInstance().setContextInternal(context);
  }

  /**
   * Get current logger statistics
   */
  public static getStats(): LoggerStats {
    return WeBelieveLoggerClass.getInstance().getStatsInternal();
  }

  /**
   * Force flush all pending logs
   */
  public static flush(): Promise<void> {
    return WeBelieveLoggerClass.getInstance().flush();
  }

  /**
   * Configure the logger (can only be called before first log)
   */
  public static configure(config: Partial<WeBelieveLoggerConfig>): void {
    const instance = WeBelieveLoggerClass.getInstance();
    instance.config = { ...instance.config, ...config };
  }

  // ============================================================
  // INSTANCE METHODS
  // ============================================================

  /**
   * Log a message with the specified level
   */
  public log(
    level: LogLevel,
    component: string,
    message: string,
    data?: Record<string, unknown>
  ): void {
    // Check if logging is enabled for this component via runtime config
    if (!LoggingConfigService.isEnabled(component)) {
      return;
    }

    // Skip debug logs in production unless explicitly enabled
    if (level === 'debug' && !this.config.enableDebug) {
      return;
    }

    const logEvent = this.createLogEvent(level, component, message, data);
    this.addToBuffer(logEvent);
    
    // Console output for development
    if (this.config.enableConsoleOutput) {
      this.outputToConsole(logEvent);
    }
  }

  /**
   * Log a performance metric
   */
  public logPerformance(
    component: string,
    operation: string,
    durationMs: number,
    data?: Record<string, unknown>
  ): void {
    // Check if logging is enabled for this component via runtime config
    if (!LoggingConfigService.isEnabled(component)) {
      return;
    }

    const logEvent = this.createLogEvent(
      'performance',
      component,
      `${operation} completed in ${durationMs}ms`,
      data,
      operation,
      durationMs
    );
    this.addToBuffer(logEvent);
    
    if (this.config.enableConsoleOutput) {
      this.outputToConsole(logEvent);
    }
  }

  /**
   * Log a performance metric with a custom source (for forwarded logs like WASM)
   */
  public logPerformanceWithSource(
    component: string,
    operation: string,
    durationMs: number,
    source: SourceLocation,
    data?: Record<string, unknown>
  ): void {
    // Check if logging is enabled for this component via runtime config
    if (!LoggingConfigService.isEnabled(component)) {
      return;
    }

    this.stats.totalLogsCreated++;
    
    const logEvent: LogEvent = {
      session_id: this.sessionId,
      user_id: this.context.user_id,
      document_id: this.context.document_id,
      realm_id: this.context.realm_id,
      timestamp: new Date().toISOString(),
      level: 'performance',
      component,
      operation,
      message: `${operation} completed in ${durationMs}ms`,
      data,
      duration_ms: durationMs,
      source,  // Use provided source instead of capturing stack trace
      browser_info: this.getBrowserInfo(),
      app_version: this.config.appVersion,
      wasm_version: this.config.wasmVersion,
    };
    
    this.addToBuffer(logEvent);
    
    if (this.config.enableConsoleOutput) {
      this.outputToConsole(logEvent);
    }
  }

  /**
   * Log a message with a custom source (for forwarded logs like WASM)
   */
  public logWithSource(
    level: LogLevel,
    component: string,
    message: string,
    source: SourceLocation,
    data?: Record<string, unknown>
  ): void {
    // Check if logging is enabled for this component via runtime config
    if (!LoggingConfigService.isEnabled(component)) {
      return;
    }

    // Skip debug logs in production unless explicitly enabled
    if (level === 'debug' && !this.config.enableDebug) {
      return;
    }

    this.stats.totalLogsCreated++;
    
    const logEvent: LogEvent = {
      session_id: this.sessionId,
      user_id: this.context.user_id,
      document_id: this.context.document_id,
      realm_id: this.context.realm_id,
      timestamp: new Date().toISOString(),
      level,
      component,
      message,
      data,
      source,  // Use provided source instead of capturing stack trace
      browser_info: this.getBrowserInfo(),
      app_version: this.config.appVersion,
      wasm_version: this.config.wasmVersion,
    };
    
    this.addToBuffer(logEvent);
    
    if (this.config.enableConsoleOutput) {
      this.outputToConsole(logEvent);
    }
  }

  /**
   * Set context for all future logs
   */
  private setContextInternal(context: LoggerContext): void {
    this.context = { ...this.context, ...context };
    this.logInternal('debug', 'logger', 'Context updated', { context: this.context });
  }

  /**
   * Get current statistics
   */
  private getStatsInternal(): LoggerStats {
    return { ...this.stats };
  }

  /**
   * Flush all pending logs to the backend
   * Chunks logs to stay under backend MAX_BATCH_SIZE (500)
   */
  public async flush(): Promise<void> {
    if (this.isFlushing || this.logBuffer.length === 0) {
      return;
    }

    const MAX_FLUSH_BATCH_SIZE = 400; // Stay under backend's 500 limit with margin

    this.isFlushing = true;
    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    try {
      // Decode source locations before sending (converts minified to original)
      const decodedLogs = await Promise.all(
        logsToSend.map(async (log) => {
          if (log.source) {
            try {
              const decoded = await decodeSourceLocation(log.source);
              return { ...log, source: decoded };
            } catch (decodeError) {
              return log;
            }
          }
          return log;
        })
      );

      // Chunk logs if needed
      if (decodedLogs.length <= MAX_FLUSH_BATCH_SIZE) {
        await this.sendBatch(decodedLogs);
        this.stats.totalLogsSent += decodedLogs.length;
        this.stats.totalBatchesSent++;
      } else {
        // Send in chunks
        for (let i = 0; i < decodedLogs.length; i += MAX_FLUSH_BATCH_SIZE) {
          const chunk = decodedLogs.slice(i, i + MAX_FLUSH_BATCH_SIZE);
          await this.sendBatch(chunk);
          this.stats.totalLogsSent += chunk.length;
          this.stats.totalBatchesSent++;
        }
      }
      this.stats.lastFlushTime = new Date().toISOString();
    } catch (error) {
      // Store failed logs in IndexedDB for retry
      if (this.config.enableOfflineStorage) {
        await this.storeOffline(logsToSend);
      } else {
        this.stats.totalLogsDropped += logsToSend.length;
      }
      this.stats.totalBatchesFailed++;
      this.stats.lastError = error instanceof Error ? error.message : String(error);
    } finally {
      this.isFlushing = false;
    }

    // Try to sync offline logs if we're back online
    if (this.config.enableOfflineStorage && navigator.onLine) {
      await this.syncOfflineLogs();
    }
  }

  /**
   * Shutdown the logger (cleanup timers, flush remaining logs)
   */
  public shutdown(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  // ============================================================
  // PRIVATE METHODS
  // ============================================================

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Parse stack trace to extract source location
   * Skips internal logger frames to find the actual caller
   */
  private getSourceLocation(skipFrames: number = 0): SourceLocation {
    try {
      const stack = new Error().stack;
      if (!stack) return { file: 'no-stack' };

      const lines = stack.split('\n');
      
      // Skip frames:
      // 0: Error
      // 1: getSourceLocation
      // 2: createLogEvent  
      // 3: log/logPerformance (instance method)
      // 4: static method (WeBelieveLogger.info/performance/etc)
      // 5+: wrapper (DocumentLogger/CacheLogger/WasmLogger) OR actual caller
      // + skipFrames for additional skipping (e.g., forwardDiagnosticsToLogger)
      
      // In production, we can't detect wrappers by name (minified).
      // So we skip extra frames to get past typical wrapper depth.
      // Logger wrappers (CanvasLogger, CacheLogger, etc.) add 1-2 frames.
      // baseSkip=8 skips: getSourceLocation, log, logInternal, static method, getInstance().log, wrapper method
      const baseSkip = 8 + skipFrames;
      let frameCount = 0;
      
      for (const line of lines) {
        // Skip the "Error" line
        if (line.trim().startsWith('Error')) {
          continue;
        }
        
        frameCount++;
        
        // Skip internal frames by count
        if (frameCount < baseSkip) {
          continue;
        }

        // Parse the stack frame - handles both Chrome and Firefox formats
        let match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/);
        if (!match) {
          match = line.match(/(.+?)@(.+?):(\d+):(\d+)/);
        }
        
        if (match) {
          const [, functionName, filePath, lineNum, colNum] = match;
          
          // Clean up the file path - extract relative path from full URL
          let file = filePath || 'unknown';
          if (file.includes('/src/')) {
            file = 'src/' + file.split('/src/').pop();
          } else if (file.includes('/node_modules/')) {
            file = file.split('/node_modules/').pop() || file;
          } else if (file.includes('/assets/')) {
            const assetMatch = file.match(/\/assets\/([^?]+)/);
            if (assetMatch) {
              file = assetMatch[1];
            }
          } else if (file.includes('https://')) {
            const urlMatch = file.match(/\/([^\/]+)$/);
            if (urlMatch) {
              file = urlMatch[1];
            }
          }
          
          return {
            file,
            line: parseInt(lineNum, 10) || 0,
            column: parseInt(colNum, 10) || 0,
            function: functionName?.trim() || undefined,
          };
        }
      }
      
      return { file: 'no-match' };
    } catch (e) {
      return { file: `error: ${e}` };
    }
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): BrowserInfo {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {
        userAgent: 'unknown',
        viewportWidth: 0,
        viewportHeight: 0,
        language: 'unknown',
        platform: 'unknown',
        online: true,
      };
    }

    return {
      userAgent: navigator.userAgent,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      language: navigator.language,
      platform: navigator.platform,
      online: navigator.onLine,
    };
  }

  /**
   * Create a log event with all metadata
   * Now captures full stack trace for all logs (premium feature)
   */
  private createLogEvent(
    level: LogLevel,
    component: string,
    message: string,
    data?: Record<string, unknown>,
    operation?: string,
    durationMs?: number
  ): LogEvent {
    this.stats.totalLogsCreated++;

    // Capture full stack trace for all logs (premium Sentry-like feature)
    // Don't skip frames - let the algorithm find user code among all frames
    // This ensures we capture React event handlers and your actual code
    const source = captureFullStack(0);

    return {
      session_id: this.sessionId,
      user_id: this.context.user_id,
      document_id: this.context.document_id,
      realm_id: this.context.realm_id,
      timestamp: new Date().toISOString(),
      level,
      component,
      operation,
      message,
      data,
      duration_ms: durationMs,
      source,
      browser_info: this.getBrowserInfo(),
      app_version: this.config.appVersion,
      wasm_version: this.config.wasmVersion,
    };
  }

  /**
   * Add a log event to the buffer and check if flush is needed
   */
  private addToBuffer(logEvent: LogEvent): void {
    this.logBuffer.push(logEvent);

    // Flush if buffer is full
    if (this.logBuffer.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * Start the periodic flush timer
   */
  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushTimer = setInterval(() => {
      if (this.logBuffer.length > 0) {
        this.flush();
      }
    }, this.config.flushIntervalMs);
  }

  /**
   * Send a batch of logs to the backend
   */
  private async sendBatch(logs: LogEvent[]): Promise<LogIngestResponse> {
    const batch: LogBatch = {
      logs,
      batch_id: this.generateSessionId(),
      batch_timestamp: new Date().toISOString(),
      batch_size: logs.length,
    };

    // Get auth token from localStorage (same as other API calls)
    const token = typeof localStorage !== 'undefined' 
      ? localStorage.getItem('webelieve_access_token') 
      : null;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(this.config.apiEndpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(batch),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Output log to console (development mode)
   */
  private outputToConsole(logEvent: LogEvent): void {
    const prefix = `[${logEvent.level.toUpperCase()}] [${logEvent.component}]`;
    const message = logEvent.operation
      ? `${prefix} ${logEvent.operation}: ${logEvent.message}`
      : `${prefix} ${logEvent.message}`;

    switch (logEvent.level) {
      case 'debug':
        console.debug(message, logEvent.data || '');
        break;
      case 'info':
        console.info(message, logEvent.data || '');
        break;
      case 'warn':
        console.warn(message, logEvent.data || '');
        break;
      case 'error':
        console.error(message, logEvent.data || '');
        break;
      case 'performance':
        console.log(`%c${message}`, 'color: #9c27b0', logEvent.data || '');
        break;
    }
  }

  /**
   * Internal logging (for logger's own events)
   */
  private logInternal(
    level: LogLevel,
    component: string,
    message: string,
    data?: Record<string, unknown>
  ): void {
    if (this.config.enableConsoleOutput) {
      const logEvent = this.createLogEvent(level, component, message, data);
      this.outputToConsole(logEvent);
    }
  }

  // ============================================================
  // OFFLINE STORAGE (IndexedDB)
  // ============================================================

  /**
   * Initialize IndexedDB for offline storage
   */
  private async initializeOfflineStorage(): Promise<void> {
    if (!this.config.enableOfflineStorage || typeof indexedDB === 'undefined') {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(INDEXEDDB_DB_NAME, INDEXEDDB_VERSION);

      request.onerror = () => {
        this.logInternal('warn', 'logger', 'Failed to open IndexedDB', {
          error: request.error?.message,
        });
        resolve(); // Don't fail initialization
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.logInternal('debug', 'logger', 'IndexedDB initialized');
        this.updateOfflineQueueSize();
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(INDEXEDDB_STORE_NAME)) {
          const store = db.createObjectStore(INDEXEDDB_STORE_NAME, { keyPath: 'id' });
          store.createIndex('created_at', 'created_at', { unique: false });
        }
      };
    });
  }

  /**
   * Store logs in IndexedDB when offline
   */
  private async storeOffline(logs: LogEvent[]): Promise<void> {
    if (!this.db) {
      this.stats.totalLogsDropped += logs.length;
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([INDEXEDDB_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(INDEXEDDB_STORE_NAME);

      for (const log of logs) {
        const entry: OfflineLogEntry = {
          id: this.generateSessionId(),
          log,
          created_at: new Date().toISOString(),
          retry_count: 0,
        };
        store.add(entry);
      }

      transaction.oncomplete = () => {
        this.stats.offlineQueueSize += logs.length;
        this.logInternal('debug', 'logger', 'Stored logs offline', { count: logs.length });
        resolve();
      };

      transaction.onerror = () => {
        this.stats.totalLogsDropped += logs.length;
        reject(transaction.error);
      };
    });
  }

  /**
   * Sync offline logs when back online
   * Chunks logs into batches to avoid exceeding backend MAX_BATCH_SIZE (500)
   */
  private async syncOfflineLogs(): Promise<void> {
    if (!this.db || this.stats.offlineQueueSize === 0) {
      return;
    }

    const MAX_SYNC_BATCH_SIZE = 400; // Stay under backend's 500 limit with margin

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([INDEXEDDB_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(INDEXEDDB_STORE_NAME);
      const request = store.getAll();

      request.onsuccess = async () => {
        const entries: OfflineLogEntry[] = request.result;
        if (entries.length === 0) {
          resolve();
          return;
        }

        // Chunk entries into batches
        const chunks: OfflineLogEntry[][] = [];
        for (let i = 0; i < entries.length; i += MAX_SYNC_BATCH_SIZE) {
          chunks.push(entries.slice(i, i + MAX_SYNC_BATCH_SIZE));
        }

        let totalSynced = 0;
        const syncedEntryIds: string[] = [];

        for (const chunk of chunks) {
          const logs = chunk.map((e) => e.log);
          try {
            await this.sendBatch(logs);
            totalSynced += logs.length;
            syncedEntryIds.push(...chunk.map((e) => e.id));
          } catch (error) {
            this.logInternal('warn', 'logger', 'Failed to sync offline log batch', {
              error: error instanceof Error ? error.message : String(error),
              batchSize: logs.length,
            });
            // Continue with next batch even if one fails
          }
        }

        // Clear synced logs
        if (syncedEntryIds.length > 0) {
          const clearTransaction = this.db!.transaction([INDEXEDDB_STORE_NAME], 'readwrite');
          const clearStore = clearTransaction.objectStore(INDEXEDDB_STORE_NAME);
          for (const id of syncedEntryIds) {
            clearStore.delete(id);
          }
          
          this.stats.totalLogsSent += totalSynced;
          this.stats.offlineQueueSize = Math.max(0, this.stats.offlineQueueSize - totalSynced);
          this.logInternal('info', 'logger', 'Synced offline logs', { 
            count: totalSynced, 
            batches: chunks.length,
            remaining: entries.length - totalSynced 
          });
        }
        resolve();
      };

      request.onerror = () => {
        resolve();
      };
    });
  }

  /**
   * Update offline queue size from IndexedDB
   */
  private async updateOfflineQueueSize(): Promise<void> {
    if (!this.db) {
      return;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([INDEXEDDB_STORE_NAME], 'readonly');
      const store = transaction.objectStore(INDEXEDDB_STORE_NAME);
      const request = store.count();

      request.onsuccess = () => {
        this.stats.offlineQueueSize = request.result;
        resolve();
      };

      request.onerror = () => {
        resolve();
      };
    });
  }
}

// Export the class and static methods
export const WeBelieveLogger = WeBelieveLoggerClass;
export default WeBelieveLogger;
